import { DateTime } from 'luxon'

import { sortArrayBy } from '@domain-group/javascript-utils'
import { useSetState } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import { ModuleState } from '@redux/modules/module'
import type {
  AvailableLiftover, PublicationModel, PublicationEditionModel,
} from '@models/types'

const dateNow = DateTime.now()

type DefaultState = {
  selectedPublicationId: number | undefined,
  selectedEditionId: number | undefined,
}

const defaultState: DefaultState = {
  selectedPublicationId: undefined,
  selectedEditionId: undefined,
}

const getPublications = (availableLiftovers: AvailableLiftover[]) => {
  const publications = availableLiftovers.reduce((acc, availableLiftover) => {
    const { publication } = availableLiftover

    // Add the publication if it doesnt exist in the object yet
    // we used the publication ID as the key
    if (!acc[publication.id]){
      acc[publication.id] = {
        ...publication,
        // @ts-expect-error TS2353
        key: publication.id,
      }
    }

    return acc
  }, {} as { [key: number]: PublicationModel })

  return sortArrayBy(Object.values(publications), 'asc', 'title')
}

type GetPublicationEditionsParams = {
  availableLiftovers: AvailableLiftover[],
  selectedPublicationId?: number,
}

const getPublicationEditions = (params: GetPublicationEditionsParams) => {
  const { availableLiftovers, selectedPublicationId } = params

  if (!selectedPublicationId){
    return []
  }

  const editions = availableLiftovers.reduce((acc, availableLiftover) => {
    const { edition } = availableLiftover

    const dateObj = edition.publication_date
      ? DateTime.fromFormat(edition.publication_date, 'dd/MM/yyyy')
      : DateTime.now()

    if (
      edition.publication_id === selectedPublicationId
      && dateObj >= dateNow
    ){
      acc.push({
        ...edition,
        // @ts-expect-error TS2353
        key: edition.id,
      })
    }

    return acc
  }, [] as PublicationEditionModel[])

  return sortArrayBy(editions, 'asc', 'title')
}

type BuildLiftoverParams = {
  availableLiftovers: AvailableLiftover[],
  selectedEditionId?: number,
  selectedPublicationId?: number,
}

const buildLiftover = (params: BuildLiftoverParams) => {
  const {
    availableLiftovers,
    selectedEditionId,
    selectedPublicationId,
  } = params

  if (!selectedEditionId){
    return null
  }

  const selectedLiftover = availableLiftovers.find(liftover => liftover.edition.id === selectedEditionId)
  if (!selectedLiftover || !selectedLiftover.unlock_product_id){
    return null
  }

  const {
    provider_price_list_item_id,
    provider_price_list_product_unlock_id,
    unlock_price_list_id,
    unlock_product_id,
  } = selectedLiftover

  return {
    provider_price_list_item_id,
    provider_price_list_product_unlock_id,
    publication_edition_id: selectedEditionId,
    publication_id: selectedPublicationId,
    unlock_price_list_id,
    unlock_product_id,
  }
}

type UseSelectOrderItemLiftoverModalOptions = {
  orderItemId: number,
  performHttpRequests?: boolean,
  requestOptions?: object,
}

function useSelectOrderItemLiftoverModal(options: UseSelectOrderItemLiftoverModalOptions) {
  const { orderItemId, performHttpRequests = true, requestOptions } = options

  const [state, setState] = useSetState(defaultState)
  const { selectedEditionId, selectedPublicationId } = state

  const { creating } = useSelector(reduxState => reduxState.orders)

  const reduxActionPayload = useReduxAction(
    'publicationEditions',
    'loadAvailableLiftovers',
    requestOptions,
    [performHttpRequests, orderItemId],
    {
      dispatchAction: action => action(orderItemId, requestOptions),
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  const { data: availableLiftovers = [], loading } = reduxActionPayload

  // @ts-expect-error TS2345
  const filteredPublications = getPublications(availableLiftovers)
  // @ts-expect-error TS2322
  const filteredEditions = getPublicationEditions({ availableLiftovers, selectedPublicationId })

  const saveEnabled = !!selectedEditionId

  return {
    availableLiftovers,
    callbacks: {
      buildLiftover: () => buildLiftover({
        // @ts-expect-error TS2322
        availableLiftovers,
        selectedEditionId,
        selectedPublicationId,
      }),
      selectEditionId: (publicationEditionId: number) => {
        setState({
          selectedEditionId: publicationEditionId,
        })
      },
      selectPublicationId: (publicationId: number) => {
        setState({
          selectedPublicationId: publicationId,
          selectedEditionId: undefined,
        })
      },
    },
    creating,
    filteredEditions,
    filteredPublications,
    loading,
    saveEnabled,
    selectedEditionId,
    selectedPublicationId,
  }
}

export default useSelectOrderItemLiftoverModal
